import { IFile } from '@repo/toolkit/types'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useRef, useState } from 'react'

import { getAffectedStaffTravelExpenses } from '../../api/affected-staffs/get-affected-staff-travel-expenses'
import { getMissionToStaffTravelExpenses } from '../../api/mission-to-staffs/get-mission-to-staff-travel-expenses'
import { mapTravelExpensesToInput } from '../../components/pages/staff/staff-personal-space/travel-expenses.utils'
import { isEmptyRow } from '../../views/staff-personal-space/staff-home/utils'
import { TravelExpenseTypeEnum } from '../types/enums/travel-expense/travel-expense.enum'
import { TravelExpense, TravelExpenseInput } from '../types/models/travel-expenses/travel-expenses'
import useAuth from './use-auth'

export const useTravelExpenses = (id: number, type: 'affected-staff' | 'mission-to-staff') => {
  const [newTravelExpenses, setNewTravelExpenses] = useState<TravelExpenseInput[]>([])
  const nextTempId = useRef(-1)
  const { user } = useAuth()

  const { refetch: refetchTravelExpenses } = useQuery({
    queryKey: ['travel-expenses', type, id],
    queryFn: async () => {
      const data: TravelExpense[] = []
      if (type === 'affected-staff') {
        const response = await getAffectedStaffTravelExpenses(id)
        data.push(...response)
      }
      if (type === 'mission-to-staff') {
        const response = await getMissionToStaffTravelExpenses(id)
        data.push(...response)
      }

      const mappedData = data.map(mapTravelExpensesToInput)
      setNewTravelExpenses(mappedData)
    },
    enabled: !!id
  })

  const preparatoryData = useMemo(
    () => newTravelExpenses?.filter((e) => e.type === TravelExpenseTypeEnum.PREPARATORY) ?? [],
    [newTravelExpenses]
  )

  const tripData = useMemo(
    () => newTravelExpenses?.filter((e) => e.type === TravelExpenseTypeEnum.TRIP) ?? [],
    [newTravelExpenses]
  )

  function deleteDocument(id: number) {
    setNewTravelExpenses((prev) =>
      prev.map((e) => (e.id === id ? { ...e, document: undefined } : e))
    )
  }

  function onDataChange(values: TravelExpenseInput[], type: TravelExpenseTypeEnum) {
    setNewTravelExpenses((prev) => [...prev.filter((e) => e.type !== type), ...values])
  }

  function addNewTravelExpense(type: TravelExpenseTypeEnum) {
    setNewTravelExpenses((prev) => [
      ...prev,
      {
        id: nextTempId.current--, // -1, -2, -3, ...
        type,
        createdByUserRole: user.role,
        validated: false
      }
    ])
  }

  function addDocument(document: IFile, id: number) {
    setNewTravelExpenses((prev) => prev.map((e) => (e.id === id ? { ...e, document } : e)))
  }

  function validateTravelExpense(id: number) {
    //when validating , if the Montant validé is empty, we set it to the Montant value
    setNewTravelExpenses((prev) =>
      prev.map((e) =>
        e.id === id ? { ...e, validated: true, validatedAmount: e.validatedAmount ?? e.amount } : e
      )
    )
  }

  function invalidateTravelExpense(id: number) {
    setNewTravelExpenses((prev) => prev.map((e) => (e.id === id ? { ...e, validated: false } : e)))
  }

  function deleteTravelExpense(id: number) {
    setNewTravelExpenses((prev) => prev.filter((e) => e.id !== id))
  }

  const isTravelExpensesValidate = (values: TravelExpenseInput[], mode: 'submit' | 'save') => {
    // Reject any completely empty row (alternatively: filter them out instead)
    if (values.some(isEmptyRow)) return false

    if (
      mode === 'submit' &&
      values.some((v) => !v.date || !v.details || !v.document || v.validatedAmount == null)
    )
      return false
    return true
  }

  return {
    travelExpenses: newTravelExpenses,
    preparatoryData,
    tripData,
    refetchTravelExpenses,
    deleteDocument,
    onDataChange,
    addNewTravelExpense,
    addDocument,
    validateTravelExpense,
    invalidateTravelExpense,
    deleteTravelExpense,
    isTravelExpensesValidate
  }
}
